/**
 * Character creation, one step at a time.
 *
 * **This was one long page of numbered sections**, and the numbering was
 * the whole of the sequencing: every field was on screen at once, so the
 * order the book gives — scores, race, gender, class, then height, weight
 * and age — was described rather than walked. It is step-by-step now.
 *
 * ⚠ **The objection the old docstring raised is real and is answered rather
 * than overruled**: *"a wizard that hides the constraints behind Next buttons
 * is one that refuses you at the end without saying which step was wrong."*
 * Three things answer it, and all three live in `pages/Wizard.tsx` and
 * `wizard.ts` rather than here — a blocked step **names the rule that blocked
 * it**, every finished step stays one click away with its choice still legible
 * in the list, and the last step is a **review of the whole character** before
 * the button that makes it.
 *
 * **Nothing here decides anything.** The options come from the server, the
 * blockers are the server's own rules read back, and the POST is checked again
 * on arrival. A greyed-out class is a courtesy; the refusal is the rule.
 *
 * **Race and class moved ahead of scores**, and the points method got its
 * own UI. The order in `characterSteps()` is now
 * *this application's own* — the book itself rolls scores first — and the
 * scores step is where the two printed qualification checks now live,
 * asked against the race and class already chosen rather than used to grey
 * out a list of buttons. See `wizard.ts`'s own docstring for the reasoning
 * and `scoreBlockers` for the check itself.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../api.js';
import { Wizard } from '../pages/Wizard.js';
import {
  adjustedScores,
  allowedAlignments,
  classBlockers,
  optionLabel,
  raceBlockers,
  rerollNote,
  type CharacterOptions,
  type Scores,
} from './options.js';
import {
  EMPTY_DRAFT,
  abilityPointsRemaining,
  abilityPointsSpent,
  affordsCsp,
  agingNotice,
  bodyRange,
  bonusLanguageBudgetFor,
  bonusLanguagesPickable,
  characterSteps,
  combatPointsAvailable,
  combatPointsSpent,
  combatPointsUnspent,
  isPointsAllocationUntouched,
  methodBlockers,
  pointsFloorScores,
  scoreBlockers,
  wholeNumber,
  type CharacterDraft,
} from './wizard.js';

const chip = (chosen: boolean, blocked = false) =>
  `rounded-md border px-3 py-1 ${
    chosen
      ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950'
      : 'border-neutral-300 dark:border-neutral-700'
  } ${blocked ? 'opacity-40' : ''}`;

const field =
  'rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900';

/**
 * SI conversion factors, not game data — Table 2.5 prints inches and pounds
 * and that stays the canonical value; these only translate a display.
 */
const CM_PER_IN = 2.54;
const KG_PER_LB = 0.45359237;

/** A whole-number text field's value, converted for display; `''` unchanged. */
function displayIn(canonical: string, units: 'imperial' | 'metric', factor: number): string {
  if (units === 'imperial' || canonical.trim() === '') return canonical;
  const value = Number(canonical);
  return Number.isFinite(value) ? String(Math.round(value * factor)) : canonical;
}

/** The reverse of `displayIn` — what a typed metric figure becomes in the canonical unit. */
function fromDisplay(typed: string, units: 'imperial' | 'metric', factor: number): string {
  if (units === 'imperial' || typed.trim() === '') return typed;
  const value = Number(typed);
  return Number.isFinite(value) ? String(Math.round(value / factor)) : typed;
}

/** Converts the `bodyRange` roll-preview text ("39–45 in") the same way. */
function convertRangeText(
  text: string,
  units: 'imperial' | 'metric',
  factor: number,
  targetUnit: string,
): string {
  if (units === 'imperial') return text;
  const match = /^(\d+)–(\d+) \w+$/.exec(text);
  if (!match?.[1] || !match[2]) return text;
  const lo = Math.round(Number(match[1]) * factor);
  const hi = Math.round(Number(match[2]) * factor);
  return `${lo}–${hi} ${targetUnit}`;
}

export function NewCharacterPage() {
  const [options, setOptions] = useState<CharacterOptions | null>(null);
  const [draft, setDraft] = useState<CharacterDraft>(EMPTY_DRAFT);
  const [at, setAt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** How many sets the server threw away to find one that fits the chosen race and class. */
  const [rollNote, setRollNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  /**
   * Whether the free-text gender box is open.
   *
   * Local rather than part of the draft, because it is not a fact about the
   * character — it is which control is on screen. Derived from the value
   * instead ("the gender is not one of the printed columns") it would be true
   * before anything had been chosen, so the page would open with "Something
   * else" already highlighted.
   */
  const [otherGender, setOtherGender] = useState(false);
  // Which tier and which name the weaponry picker is currently showing. Local,
  // because they are a control's position and not a fact about the character —
  // what is bought lands in the draft when Train is pressed.
  const [weaponryTier, setWeaponryTier] = useState('singleWeapon');
  const [weaponryName, setWeaponryName] = useState('');
  // ⚠ **Display only — the draft, the roll and the server all stay
  // inches/pounds.** Table 2.5 prints in/lb and migration 0025's columns are
  // `height_in`/`weight_lb`; a metric *draft* would mean converting on every
  // read and write for the life of the character, and rounding twice instead
  // of once. This is a control's position, the same footing as the weaponry
  // picker above — what changes is only what an already-canonical number is
  // shown as.
  const [bodyUnits, setBodyUnits] = useState<'imperial' | 'metric'>('imperial');

  const change = (patch: Partial<CharacterDraft>) => setDraft((was) => ({ ...was, ...patch }));

  useEffect(() => {
    void api
      .get<CharacterOptions>('/api/character-options')
      .then((body) => {
        setOptions(body);
        change({ method: body.generationMethods[0]?.key ?? '' });
      })
      .catch(() => setError('the character options could not be loaded'));
  }, []);

  if (!options) {
    return (
      <p className="text-sm" role="status">
        {error ?? 'Loading…'}
      </p>
    );
  }

  const raceOption = options.races.find((r) => r.race === draft.race);
  const classOption = options.classes.find((c) => c.class === draft.characterClass);
  const methodOption = options.generationMethods.find((m) => m.key === draft.method);
  const adjusted = draft.rolled ? adjustedScores(draft.rolled, raceOption) : null;
  const scoreProblems = scoreBlockers(draft, options);
  const alignmentChoices = classOption
    ? allowedAlignments(classOption.alignment, options.alignments)
    : options.alignments;
  const columns = options.bodyColumns ?? [];
  const steps = characterSteps(draft, options);
  const age = wholeNumber(draft.age);
  const aging = agingNotice(options, draft.race, age);
  const ageTable = options.bodies?.find((one) => one.race === draft.race)?.age;
  const range = bodyRange(options, draft.race, draft.bodyColumn);
  const combat = options.combatSkills;
  const cspAvailable = combatPointsAvailable(draft, options);
  const cspSpent = combatPointsSpent(draft, options);
  const cspLeft = combatPointsUnspent(draft, options);

  /**
   * Rolls scores, or a points budget — the race and class are chosen already,
   * so nothing here clears them. Qualification is now asked the other way
   * round: `scoreBlockers` (via the scores step's own `blockedBy`) says
   * whether *this* roll fits what was picked, and the player is free to roll
   * again, or navigate back and change the race or class, as often as they
   * like — the same latitude "Roll again" always had, just checked against a
   * fixed target instead of narrowing a list of buttons.
   */
  const rollScores = () => {
    setError(null);
    setRollNote(null);
    // **Rolled for the race and class already chosen**: the server throws away sets that cannot qualify
    // (this application's rule, not the book's) and says how many it threw away.
    const forWho =
      methodOption?.kind !== 'points' && draft.race !== ''
        ? { race: draft.race, ...(draft.characterClass !== '' ? { classes: [draft.characterClass] } : {}) }
        : {};
    void api
      .post<{ scores?: number[]; points?: number; kind: 'roll' | 'points'; rerolls?: number }>(
        '/api/character-rolls',
        { method: draft.method, ...forWho },
      )
      .then((body) => {
        if (body.rerolls !== undefined) setRollNote(rerollNote(body.rerolls, draft.race, draft.characterClass));
        if (body.kind === 'points') {
          change({
            pointsBudget: body.points ?? 0,
            rolled: pointsFloorScores(options, methodOption ?? {}),
            exceptionalStrength: '',
          });
          return;
        }
        const next: Scores = {};
        options.abilities.forEach((ability, index) => {
          next[ability] = body.scores?.[index] ?? 0;
        });
        change({ rolled: next, pointsBudget: null, exceptionalStrength: '' });
      })
      .catch(() => setError('the roll could not be made'));
  };

  /**
   * One ability's allocation, changed by hand rather than by dice.
   *
   * Clamped to the method's own printed floor and ceiling here, as a
   * courtesy — the server never trusted a submitted score to have come from
   * any particular method before this, and does not start now; the real
   * bound is `parseStats`'s 1–25, checked on arrival like everything else.
   */
  const allocate = (ability: string, value: number) => {
    const bounds = methodOption?.pointsBounds;
    const clamped = bounds ? Math.min(bounds.max, Math.max(bounds.min, value)) : value;
    change({
      rolled: { ...(draft.rolled ?? {}), [ability]: clamped },
      // Exceptional Strength only exists at Strength 18 — moving Strength
      // away from it leaves a spend with nothing left to attach to.
      ...(ability === 'str' && clamped !== 18 ? { exceptionalStrength: '' } : {}),
    });
  };

  /**
   * Trades two abilities' rolled values — the "assign as player chooses"
   * methods' own control (Method II and III; see `assignPlayerChooses` in
   * `packages/rules-data`). `rollScores` still lands the six numbers on
   * STR/DEX/CON/INT/WIS/CHA in printed order the instant they're rolled, the
   * same way it always has — a swap is just how the player moves one from
   * there to where they actually want it, without ever leaving an ability
   * unfilled or a rolled number unused.
   */
  const swapScores = (a: string, b: string) => {
    const rolled = draft.rolled;
    if (!rolled || a === b) return;
    const av = rolled[a];
    const bv = rolled[b];
    if (av === undefined || bv === undefined) return;
    change({ rolled: { ...rolled, [a]: bv, [b]: av } });
  };

  /**
   * Height, weight and age, off Table 2.5 and Table 2.6.
   *
   * **The column is sent explicitly.** Table 2.5 prints two base figures per
   * race and the server refuses a request that names neither — silently taking
   * the first would make every character in the game the size of a man.
   */
  const rollBody = () => {
    setError(null);
    void api
      .post<{ heightIn: number; weightLb: number; age: number }>('/api/character-rolls/body', {
        race: draft.race,
        column: draft.bodyColumn,
      })
      .then((body) =>
        change({
          heightIn: String(body.heightIn),
          weightLb: String(body.weightLb),
          age: String(body.age),
        }),
      )
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'the body could not be rolled'),
      );
  };

  const create = () => {
    if (!draft.rolled || !raceOption || !classOption) return;
    setBusy(true);
    setError(null);
    void api
      .post<{ character: { id: string } }>('/api/characters', {
        name: draft.name.trim(),
        race: draft.race,
        alignment: draft.alignment,
        // **Optional, and the server writes one when it is left blank.** Left
        // empty here the DM writes it once at creation; either way it is then
        // fixed, because a description re-derived per beat is what gave a human
        // mage fur and antennae.
        ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
        // What the character IS — Tables 2.5 and 2.6 plus the gender field.
        // The aging adjustment is the server's: it applies the printed steps to
        // the scores on the way in, cumulatively, and a client that did the
        // arithmetic would be a second copy of a table.
        ...(draft.gender.trim() ? { gender: draft.gender.trim() } : {}),
        ...(age !== undefined ? { age } : {}),
        ...(wholeNumber(draft.heightIn) !== undefined
          ? { heightIn: wholeNumber(draft.heightIn) }
          : {}),
        ...(wholeNumber(draft.weightLb) !== undefined
          ? { weightLb: wholeNumber(draft.weightLb) }
          : {}),
        stats: {
          strength: draft.rolled['str'],
          dexterity: draft.rolled['dex'],
          constitution: draft.rolled['con'],
          intelligence: draft.rolled['int'],
          wisdom: draft.rolled['wis'],
          charisma: draft.rolled['cha'],
          // Only meaningful at Strength 18, and the points allocation UI
          // already clears it the moment Strength moves off 18 — this is the
          // same "send it only when it means something" rule the age and
          // body fields use.
          ...(wholeNumber(draft.exceptionalStrength) !== undefined
            ? { exceptionalStrength: wholeNumber(draft.exceptionalStrength) }
            : {}),
        },
        // **The loadout, not a total.** `combatSkillPointsSpent` is derived
        // server-side from exactly these three fields — a total a client sent
        // would be a budget a client set — and the eligibility rules are
        // re-checked there, because the choice is permanent.
        skills: {
          combatSkillPointsSpent: 0,
          nonCombatSkillPointsSpent: 0,
          ...(draft.weaponSkills.length > 0 ? { weaponSkills: draft.weaponSkills } : {}),
          ...(draft.combatMethods.length > 0 ? { combatMethods: draft.combatMethods } : {}),
          ...(draft.ambidextrous ? { ambidextrous: true } : {}),
          ...(draft.specialisedIn ? { specialisedIn: draft.specialisedIn } : {}),
        },
        // No hit points and no adjusted scores: the server rolls the class
        // hit die with the Constitution adjustment, and applies the racial
        // adjustment to the scores as rolled. Both are rules, and a client
        // that sent them would be inventing the numbers a new character is
        // made of.
        // No armour class either: it is what the character is wearing plus
        // its Dexterity, and the server derives it from the inventory.
        classes: [{ class: draft.characterClass, level: 1 }],
        ...(draft.characterClass === 'specialistMage'
          ? { specialistSchool: draft.specialistSchool }
          : {}),
        ...(draft.characterClass === 'mage' || draft.characterClass === 'specialistMage'
          ? { spellbookType: draft.spellbookType }
          : {}),
        ...(draft.bonusLanguages.length > 0 ? { bonusLanguages: draft.bonusLanguages } : {}),
      })
      // ⚠ **It used to navigate straight to the sheet, and that was the end of
      // the waterfall.** Making a character and making a campaign are separate
      // acts now, so finishing one leaves you holding a character and no table
      // — and nothing said what to do about it. The campaign wizard already
      // ends by naming the steps left; this is the same ending.
      .then((body) => setCreated({ id: body.character.id, name: draft.name.trim() }))
      // The server's own words. It knows which rule was broken and this does
      // not — paraphrasing it here would be a second, worse copy of the rules.
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'the character was refused'))
      .finally(() => setBusy(false));
  };

  if (created) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">{created.name}</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {/* Says what is true rather than naming a role: the character exists
              and is at no table, which is a real state now rather than a
              half-finished one. */}
          Rolled up, and not yet at a table.
        </p>

        <section className="space-y-2 rounded-md border border-neutral-200 p-4 dark:border-neutral-800">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
            What next
          </h2>
          <ul className="space-y-1 text-sm">
            <li>
              <Link to="/" className="underline underline-offset-2">
                Take a seat at a table
              </Link>{' '}
              <span className="text-neutral-600 dark:text-neutral-300">
                — the lobby lists the ones you can join.
              </span>
            </li>
            <li>
              <Link to="/campaigns/new" className="underline underline-offset-2">
                Start a table of your own
              </Link>{' '}
              <span className="text-neutral-600 dark:text-neutral-300">
                — you can bring this character, or set it up for other people.
              </span>
            </li>
            <li>
              <Link to={`/characters/${created.id}`} className="underline underline-offset-2">
                Look at the sheet
              </Link>
            </li>
          </ul>
        </section>
      </div>
    );
  }

  const here = steps[at]?.key;

  return (
    <div className="space-y-5">
      <h1 className="text-lg font-semibold">New character</h1>

      {error && (
        <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
          {error}
        </p>
      )}

      <Wizard
        steps={steps}
        current={at}
        onGo={setAt}
        finish={{ label: busy ? 'Creating…' : 'Create character', onPress: create, busy }}
      >
        {/* ⚠ **One step, on purpose.** The class list is filtered by
            whichever race is picked immediately above it — a race can
            forbid a class outright
            — so seeing the effect of one choice on the other used to mean
            leaving the screen. */}
        {here === 'raceClass' && (
          <div className="space-y-5">
            {/* Named groups, not just headings — the two lists share one
                step now, so an assistive technology (and `e2e`'s own
                `takeFirstOffered`) needs a way to ask for "the race options"
                distinctly from "the class options" within it. */}
            <div className="space-y-2" role="group" aria-label="Race">
              <p className="text-sm font-medium">Race</p>
              {/* **Nothing to qualify against yet.** The scores come later
                  now — this list is wide open until you roll or allocate
                  them, and the scores step then greys against whichever race
                  and class are picked here, in the two directions the book
                  prints: a race on the roll **as rolled**, a class on it
                  **after** the racial adjustment. Coming back here after
                  rolling still greys out anything the roll no longer fits. */}
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                Pick freely. Once you roll or allocate ability scores, this list — and the class
                list below it — will grey out anything that no longer fits, and you can always
                come back and change it.
              </p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {options.races.map((option) => {
                  const blockers = draft.rolled ? raceBlockers(option, draft.rolled) : [];
                  return (
                    <li key={option.race}>
                      <button
                        type="button"
                        disabled={blockers.length > 0}
                        title={blockers.join('; ')}
                        onClick={() =>
                          change({
                            race: option.race,
                            characterClass: '',
                            alignment: '',
                            specialistSchool: '',
                            // Table 2.5's bases are per race, so a height rolled
                            // for a halfling is not a height for a human.
                            heightIn: '',
                            weightLb: '',
                            age: '',
                          })
                        }
                        className={chip(draft.race === option.race, blockers.length > 0)}
                      >
                        {optionLabel(option.race)}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {adjusted && draft.rolled && (
                <p className="text-xs tabular-nums text-neutral-600 dark:text-neutral-300">
                  {options.abilities
                    .map((ability) =>
                      adjusted[ability] === draft.rolled?.[ability]
                        ? `${ability} ${adjusted[ability]}`
                        : `${ability} ${draft.rolled?.[ability]} → ${adjusted[ability]}`,
                    )
                    .join(' · ')}
                </p>
              )}
            </div>

            <div className="space-y-2" role="group" aria-label="Class">
              <p className="text-sm font-medium">Class</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                A class this race allows — that half does not wait for a roll. Once there are
                scores, the ability requirements join in too, checked <em>after</em> the racial
                adjustment (the opposite way round to the race). A greyed class says why when you
                hover it.
              </p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {options.classes.map((option) => {
                  const blockers = classBlockers(option, raceOption, adjusted);
                  const limit = raceOption?.classes.find((c) => c.class === option.class)?.limit;
                  return (
                    <li key={option.class}>
                      <button
                        type="button"
                        disabled={blockers.length > 0}
                        title={blockers.join('; ')}
                        onClick={() =>
                          change({
                            characterClass: option.class,
                            alignment: '',
                            specialistSchool: '',
                            // The budget and the method rules are both per class,
                            // so a loadout bought as a fighter is not one a mage
                            // may hold — and the points are not the same number.
                            weaponSkills: [],
                            combatMethods: [],
                            ambidextrous: false,
                            specialisedIn: '',
                          })
                        }
                        className={chip(draft.characterClass === option.class, blockers.length > 0)}
                      >
                        {optionLabel(option.class)}
                        {/* The level cap is part of choosing, not a surprise at
                            level 12. */}
                        {typeof limit === 'number' && (
                          <span className="text-xs text-neutral-600 dark:text-neutral-300">
                            {' '}
                            · to {limit}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* A mage keeps a spellbook, and which kind decides how many spells it
                  holds and what each page costs to copy into. Standard unless they
                  choose, so this never blocks a step. The figures are the server's. */}
              {(draft.characterClass === 'mage' || draft.characterClass === 'specialistMage') &&
                (options.spellbooks?.length ?? 0) > 0 && (
                  <fieldset className="space-y-1">
                    <legend className="text-xs text-neutral-600 dark:text-neutral-300">
                      Spellbook — what you keep your spells in
                    </legend>
                    <div className="flex flex-wrap gap-2">
                      {options.spellbooks!.map((book) => (
                        <button
                          key={book.type}
                          type="button"
                          aria-pressed={draft.spellbookType === book.type}
                          onClick={() => change({ spellbookType: book.type })}
                          className={`rounded-md border px-3 py-1 text-sm ${
                            draft.spellbookType === book.type
                              ? 'border-neutral-900 bg-neutral-900 text-white dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900'
                              : 'border-neutral-300 dark:border-neutral-700'
                          }`}
                        >
                          {optionLabel(book.type)} · {book.pages} pages · {book.costPerPage} a page to copy
                        </button>
                      ))}
                    </div>
                  </fieldset>
                )}

              {/* A specialist has to name a school, because the bonus slot the class
                  grants is usable only for that school. The list is the server's:
                  not every school qualifies — Lesser Divination is castable by any
                  wizard and names no specialist. */}
              {draft.characterClass === 'specialistMage' && (
                <div className="space-y-1">
                  <label
                    className="text-xs text-neutral-600 dark:text-neutral-300"
                    htmlFor="specialist-school"
                  >
                    School of specialty — one extra spell slot per level, for this school only
                  </label>
                  <select
                    id="specialist-school"
                    value={draft.specialistSchool}
                    onChange={(e) => change({ specialistSchool: e.target.value })}
                    className="block rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                  >
                    <option value="">choose a school…</option>
                    {(options.specialistSchools ?? []).map((school) => (
                      <option key={school} value={school}>
                        {optionLabel(school)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {here === 'alignment' && (
          <div className="space-y-2">
            {classOption && classOption.alignment !== 'any' && (
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                {optionLabel(classOption.class)}: {classOption.alignment}
              </p>
            )}
            <ul className="flex flex-wrap gap-2 text-sm">
              {alignmentChoices.map((option) => (
                <li key={option}>
                  <button
                    type="button"
                    onClick={() => change({ alignment: option })}
                    className={chip(draft.alignment === option)}
                  >
                    {optionLabel(option)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {here === 'scores' && (
          <div className="space-y-2">
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              Qualified against the {draft.race ? optionLabel(draft.race) : 'race'}{' '}
              {draft.characterClass ? optionLabel(draft.characterClass) : 'class'} chosen above.
              Reroll, reallocate, or go back and change either — as often as you like, the same
              latitude a straight roll always had.
            </p>
            <div className="space-y-1">
              <label className="block text-sm font-medium" htmlFor="method">
                Generation method
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  id="method"
                  value={draft.method}
                  onChange={(e) =>
                    change({
                      method: e.target.value,
                      rolled: null,
                      pointsBudget: null,
                      exceptionalStrength: '',
                    })
                  }
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  {/* ⚠ **The book's own name, not the printed sentence.**
                      All four in one `<select>` of raw dice notation
                      ("roll 4d4+2 six times, assign as player chooses") reads
                      as noise with nothing to scan; `m.label` is the same
                      "Method I/II/III/IV" numbering and nickname the book
                      itself uses for these four. The full sentence still
                      shows below, once one is picked. */}
                  {options.generationMethods.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={rollScores}
                  className="rounded-md border border-neutral-300 px-3 py-1 text-sm font-medium dark:border-neutral-700"
                >
                  {methodOption?.kind === 'points'
                    ? draft.pointsBudget !== null
                      ? 'Reroll budget'
                      : 'Roll budget'
                    : draft.rolled
                      ? 'Roll again'
                      : 'Roll'}
                </button>
              </div>
              {methodOption && (
                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                  {methodOption.description}
                </p>
              )}
              {rollNote && (
                <p role="status" className="text-xs text-neutral-600 dark:text-neutral-300">
                  {rollNote}
                </p>
              )}
            </div>

            {methodOption?.kind === 'points' ? (
              draft.pointsBudget !== null && (
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="tabular-nums font-medium">
                      {abilityPointsSpent(draft, options)}/{draft.pointsBudget}
                    </span>{' '}
                    <span className="text-neutral-600 dark:text-neutral-300">points spent</span>
                    {abilityPointsRemaining(draft, options) < 0 && (
                      <span className="ml-2 text-amber-800 dark:text-amber-300">over budget</span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {options.abilities.map((ability) => (
                      <label key={ability} className="space-y-1">
                        <span className="block text-xs uppercase text-neutral-600 dark:text-neutral-300">
                          {ability}
                        </span>
                        <input
                          type="number"
                          min={methodOption.pointsBounds?.min}
                          max={methodOption.pointsBounds?.max}
                          value={draft.rolled?.[ability] ?? methodOption.pointsBounds?.min ?? 0}
                          onChange={(e) => allocate(ability, Number(e.target.value))}
                          className={`w-16 ${field}`}
                        />
                      </label>
                    ))}
                  </div>
                  {methodOption.pointsBounds && (
                    <p className="text-xs text-neutral-600 dark:text-neutral-300">
                      {methodOption.pointsBounds.min}–{methodOption.pointsBounds.max} per score, as
                      printed.
                    </p>
                  )}
                  {/* Exceptional Strength is the points method's own extra
                      spend, and only makes sense for exactly the case the
                      book restricts it to: a warrior with Strength 18. */}
                  {draft.rolled?.['str'] === 18 &&
                    methodOption.exceptionalStrengthPercentPerPoint &&
                    classOption?.combatSkillGroup === 'warrior' && (
                      <label className="block space-y-1">
                        <span className="block text-xs text-neutral-600 dark:text-neutral-300">
                          Exceptional Strength (%) — warriors only, and only at Strength 18; each{' '}
                          {methodOption.exceptionalStrengthPercentPerPoint}% costs 1 point
                        </span>
                        <input
                          value={draft.exceptionalStrength}
                          onChange={(e) => change({ exceptionalStrength: e.target.value })}
                          inputMode="numeric"
                          className={`w-20 ${field}`}
                        />
                      </label>
                    )}
                </div>
              )
            ) : draft.rolled && methodOption?.assignPlayerChooses ? (
              // ⚠ **The method that says "assign as player chooses" used to
              // give no way to.** The six values still land
              // in printed order the instant they're rolled — this is only
              // how they get moved from there to wherever the player wants,
              // via `swapScores`, so every ability keeps exactly one of the
              // six rolled numbers throughout.
              <div className="space-y-2">
                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                  Six scores were rolled. Swap any two to place them where you want.
                </p>
                <div className="flex flex-wrap gap-3 text-sm">
                  {options.abilities.map((ability) => (
                    <div key={ability} className="space-y-1">
                      <span className="block text-xs uppercase text-neutral-600 dark:text-neutral-300">
                        {ability}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="w-6 tabular-nums font-medium">{draft.rolled?.[ability]}</span>
                        <select
                          aria-label={`Swap ${optionLabel(ability)} with`}
                          value=""
                          onChange={(e) => {
                            if (e.target.value) swapScores(ability, e.target.value);
                          }}
                          className="rounded-md border border-neutral-300 bg-white px-1 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                        >
                          <option value="">Swap…</option>
                          {options.abilities
                            .filter((other) => other !== ability)
                            .map((other) => (
                              <option key={other} value={other}>
                                {optionLabel(other)} ({draft.rolled?.[other]})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              draft.rolled && (
                <table className="text-sm">
                  <tbody>
                    <tr>
                      {options.abilities.map((ability) => (
                        <th key={ability} className="px-2 text-left font-medium uppercase">
                          {ability}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {options.abilities.map((ability) => (
                        <td key={ability} className="px-2 tabular-nums">
                          {draft.rolled?.[ability]}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              )
            )}

            {/* ⚠ **Said in the words of the rule that stopped it**, the same
                courtesy every other blocked step gets — `scoreBlockers` is
                the pair of functions that used to grey out race and class
                buttons, asked instead against the fixed target chosen above.
                **Suppressed while a fresh points allocation sits untouched at
                the floor** — the exact same guard as the step's own
                `blockedBy` text, read from the one shared function rather
                than repeated, because this was the second of two places that
                asked the same question and only one got fixed at first. */}
            {draft.rolled && !isPointsAllocationUntouched(draft, options) && scoreProblems.length > 0 && (
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Does not qualify: {scoreProblems.join('; ')}.
              </p>
            )}
          </div>
        )}

        {here === 'languages' && (
          <div className="space-y-2">
            {(() => {
              const budget = bonusLanguageBudgetFor(draft, options);
              const pool = bonusLanguagesPickable(draft, options);
              if (budget === 0) {
                return (
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    This Intelligence grants no bonus languages beyond what{' '}
                    {optionLabel(draft.race)} already starts with.
                  </p>
                );
              }
              return (
                <>
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    Pick up to {budget} bonus language{budget === 1 ? '' : 's'}, beyond what{' '}
                    {optionLabel(draft.race)} already starts with.{' '}
                    {options.bonusLanguages?.rule}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {pool.map((language) => {
                      const picked = draft.bonusLanguages.includes(language);
                      const disabled = !picked && draft.bonusLanguages.length >= budget;
                      return (
                        <button
                          key={language}
                          type="button"
                          disabled={disabled}
                          aria-pressed={picked}
                          onClick={() =>
                            change({
                              bonusLanguages: picked
                                ? draft.bonusLanguages.filter((l) => l !== language)
                                : [...draft.bonusLanguages, language],
                            })
                          }
                          className={chip(picked, disabled)}
                        >
                          {optionLabel(language)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-neutral-600 dark:text-neutral-300">
                    {draft.bonusLanguages.length} of {budget} picked.
                  </p>
                </>
              );
            })()}
          </div>
        )}

        {here === 'name' && (
          <input
            value={draft.name}
            onChange={(e) => change({ name: e.target.value })}
            aria-label="Character name"
            placeholder="Ilra Fenn"
            className={`w-64 ${field}`}
          />
        )}

        {here === 'body' && (
          <div className="space-y-4">
            {/* ⚠ **The step the sheet had nothing for at all until
                2026-08-30.** Tables 2.5 and 2.6 were extracted on the first
                pass and read by nothing, so a gender, an age, a height and a
                weight had nowhere to live — and the DM invented all four,
                differently, every beat. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              The book's own order puts this after the class: <em>"the player should choose his
              character's height, weight, and age or roll for them randomly"</em>. Recorded
              because otherwise the Dungeon Master invents them afresh in every scene.
            </p>

            <div className="space-y-2">
              <p className="text-sm font-medium">Gender</p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {columns.map((column) => (
                  <li key={column}>
                    <button
                      type="button"
                      onClick={() => {
                        setOtherGender(false);
                        change({
                          gender: column,
                          bodyColumn: column,
                          heightIn: '',
                          weightLb: '',
                        });
                      }}
                      className={chip(!otherGender && draft.gender === column)}
                    >
                      {optionLabel(column)}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    // Clears only what it is switching away from. Pressed while
                    // the box is already open it would otherwise wipe whatever
                    // had been typed into it.
                    onClick={() => {
                      if (!otherGender) change({ gender: '', bodyColumn: '', heightIn: '', weightLb: '' });
                      setOtherGender(true);
                    }}
                    className={chip(otherGender)}
                  >
                    Something else
                  </button>
                </li>
              </ul>
              {/* ⚠ **A column and a gender are two questions.** Table 2.5 prints
                  its bases under two headings and that decides which row a
                  *roll* starts from; it does not decide what somebody is. So
                  anything may be written here, and if it is not one of the two
                  printed columns the roll has to be told which row to use. */}
              {otherGender && (
                <div className="space-y-2">
                  <input
                    value={draft.gender}
                    onChange={(e) => change({ gender: e.target.value })}
                    aria-label="Gender"
                    maxLength={32}
                    placeholder="in your own words"
                    className={`w-64 ${field}`}
                  />
                  <p className="text-xs text-neutral-600 dark:text-neutral-300">
                    The book prints its base heights and weights in two columns,{' '}
                    {columns.join(' and ')}, and the dice have to start from one of them. Which
                    row this character is built on:
                  </p>
                  <ul className="flex flex-wrap gap-2 text-sm">
                    {columns.map((column) => (
                      <li key={column}>
                        <button
                          type="button"
                          onClick={() => change({ bodyColumn: column, heightIn: '', weightLb: '' })}
                          className={chip(draft.bodyColumn === column)}
                        >
                          {optionLabel(column)} row
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={draft.race === '' || draft.bodyColumn === ''}
                  title={
                    draft.bodyColumn === ''
                      ? 'the dice start from one of the two printed columns — choose one above'
                      : undefined
                  }
                  onClick={rollBody}
                  className="rounded-md border border-neutral-300 px-3 py-1 text-sm font-medium disabled:opacity-40 dark:border-neutral-700"
                >
                  Roll height, weight and age
                </button>
                {range && (
                  <span className="text-xs text-neutral-600 dark:text-neutral-300">
                    {convertRangeText(range.height, bodyUnits, CM_PER_IN, 'cm')},{' '}
                    {convertRangeText(range.weight, bodyUnits, KG_PER_LB, 'kg')}
                    {ageTable
                      ? `, ${ageTable.startingRange.min}–${ageTable.startingRange.max} years`
                      : ''}
                  </span>
                )}
              </div>

              {/* ⚠ **Table 2.5 prints inches and pounds; a metric player
                  shouldn't have to convert by hand to use them.**
                  Display-only, so it can be flipped freely without touching
                  what is actually stored — see the note on `bodyUnits`. */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-neutral-600 dark:text-neutral-300">Units</span>
                <div className="inline-flex overflow-hidden rounded-md border border-neutral-300 dark:border-neutral-700">
                  {(['imperial', 'metric'] as const).map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      onClick={() => setBodyUnits(unit)}
                      aria-pressed={bodyUnits === unit}
                      className={`px-2 py-1 ${
                        bodyUnits === unit
                          ? 'bg-emerald-50 font-medium dark:bg-emerald-950'
                          : ''
                      }`}
                    >
                      {unit === 'imperial' ? 'in / lb' : 'cm / kg'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 text-sm">
                <label className="space-y-1">
                  <span className="block text-xs text-neutral-600 dark:text-neutral-300">
                    Age (years)
                  </span>
                  <input
                    value={draft.age}
                    onChange={(e) => change({ age: e.target.value })}
                    inputMode="numeric"
                    aria-label="Age in years"
                    className={`w-24 ${field}`}
                  />
                </label>
                <label className="space-y-1">
                  <span className="block text-xs text-neutral-600 dark:text-neutral-300">
                    Height ({bodyUnits === 'imperial' ? 'inches' : 'cm'})
                  </span>
                  <input
                    value={displayIn(draft.heightIn, bodyUnits, CM_PER_IN)}
                    onChange={(e) =>
                      change({ heightIn: fromDisplay(e.target.value, bodyUnits, CM_PER_IN) })
                    }
                    inputMode="numeric"
                    aria-label={bodyUnits === 'imperial' ? 'Height in inches' : 'Height in centimetres'}
                    className={`w-24 ${field}`}
                  />
                </label>
                <label className="space-y-1">
                  <span className="block text-xs text-neutral-600 dark:text-neutral-300">
                    Weight ({bodyUnits === 'imperial' ? 'lb' : 'kg'})
                  </span>
                  <input
                    value={displayIn(draft.weightLb, bodyUnits, KG_PER_LB)}
                    onChange={(e) =>
                      change({ weightLb: fromDisplay(e.target.value, bodyUnits, KG_PER_LB) })
                    }
                    inputMode="numeric"
                    aria-label={bodyUnits === 'imperial' ? 'Weight in pounds' : 'Weight in kilograms'}
                    className={`w-24 ${field}`}
                  />
                </label>
              </div>

              {/* ⚠ **Age is the one of the four that moves a number**, and the
                  steps are cumulative. Said before the character is made,
                  because the alternative is a player finding their Strength
                  four lower than the figure they rolled with nothing on screen
                  to say why. */}
              {aging && (
                <p className="text-sm text-amber-800 dark:text-amber-300">{aging}</p>
              )}
              {ageTable && !aging && (
                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                  Age costs nothing until {ageTable.steps[0]?.beginsAt}, where the book's first
                  aging step begins. Older than that is allowed and priced.
                </p>
              )}
            </div>
          </div>
        )}

        {here === 'combat' && (
          <div className="space-y-6">
            <div className="space-y-2">
              {/* ⚠ **"CSP" was never spelled out anywhere on this step.**
                  `combat?.rule` is the printed sentence and it starts mid-abbreviation, because
                  the book assumes a reader who already knows the term. This
                  line is ours, not the book's, and exists only to say what
                  the three letters mean before the rule itself uses them. */}
              <p className="text-sm">
                <strong>Combat Skill Points (CSP)</strong> buy training with weapons and the
                combat methods below.
              </p>
              {/* ⚠ **Nothing in this application could spend a Combat Skill
                  Point, for a while.** The flow sent a spend of zero and
                  there was no control that could change it, so every character
                  ever made here threw its whole opening allowance away — while
                  the other half of the rule, the penalty for swinging something
                  you were never trained with, had no reader either. */}
              <p className="text-xs text-neutral-600 dark:text-neutral-300">{combat?.rule}</p>
              <p className="text-sm">
                <span className="tabular-nums font-medium">
                  {cspSpent}/{cspAvailable}
                </span>{' '}
                <span className="text-neutral-600 dark:text-neutral-300">CSP spent</span>
                {/* ⚠ **Nothing here used to say so.** Weapon training,
                    specialisation and Ambidexterity spent CSP with no check
                    against the budget at all — only a combat *method* did —
                    so this could read "3/2" with no visible sign anything
                    was wrong. All three now refuse a spend the budget
                    can't cover, so this should be unreachable; kept as the
                    same safety net the points-buy score step already has. */}
                {cspSpent > cspAvailable && (
                  <span className="ml-2 text-amber-800 dark:text-amber-300">over budget</span>
                )}
                {classOption?.unskilledPenalty && (
                  <span className="ml-2 text-xs text-neutral-600 dark:text-neutral-300">
                    · untrained, this class is at {classOption.unskilledPenalty.unrelated} to hit (
                    {classOption.unskilledPenalty.sameGroup} with a weapon in the same group)
                  </span>
                )}
              </p>
              {/* ⚠ **The irreversible half, said before it happens.** Unspent
                  points at 1st level are gone — not banked — and that is
                  invisible everywhere else. */}
              {cspLeft > 0 && (
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  {cspLeft} unspent. Points left over at 1st level are <strong>lost</strong> — they
                  stand for training you did before the campaign began, so there is nothing to
                  save them for.
                </p>
              )}
            </div>

            <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <p className="text-sm font-medium">Weapons you are trained with</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                {combat?.tiers
                  .map((tier) => `${optionLabel(tier.tier)} ${tier.cost}`)
                  .join(' · ')}
                . {combat?.ungroupedWeapons}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor="weaponry-tier">
                  Weaponry tier
                </label>
                <select
                  id="weaponry-tier"
                  value={weaponryTier}
                  onChange={(e) => {
                    setWeaponryTier(e.target.value);
                    setWeaponryName('');
                  }}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  {(combat?.tiers ?? []).map((tier) => (
                    <option key={tier.tier} value={tier.tier}>
                      {optionLabel(tier.tier)} — {tier.cost}
                    </option>
                  ))}
                </select>
                <label className="sr-only" htmlFor="weaponry-name">
                  What to train in
                </label>
                <select
                  id="weaponry-name"
                  value={weaponryName}
                  onChange={(e) => setWeaponryName(e.target.value)}
                  className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <option value="">choose…</option>
                  {/* Each tier answers in its own currency — a weapon, one of
                      Table 5.2's groups, or a damage type — which is why the
                      list changes with the tier rather than being one list. */}
                  {(weaponryTier === 'singleWeapon'
                    ? (combat?.weapons ?? [])
                    : weaponryTier === 'weaponryGroup'
                      ? Object.keys(combat?.groups ?? {})
                      : (combat?.damageTypes ?? [])
                  ).map((name) => (
                    <option key={name} value={name}>
                      {optionLabel(name)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={
                    weaponryName === '' ||
                    !affordsCsp(
                      draft,
                      options,
                      combat?.tiers.find((tier) => tier.tier === weaponryTier)?.cost ?? 0,
                    )
                  }
                  title={
                    weaponryName !== '' &&
                    !affordsCsp(
                      draft,
                      options,
                      combat?.tiers.find((tier) => tier.tier === weaponryTier)?.cost ?? 0,
                    )
                      ? 'not enough combat skill points left'
                      : undefined
                  }
                  onClick={() =>
                    change({
                      weaponSkills: [
                        ...draft.weaponSkills,
                        { name: weaponryName, tier: weaponryTier },
                      ],
                    })
                  }
                  className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-neutral-700"
                >
                  Train
                </button>
              </div>
              {draft.weaponSkills.length > 0 && (
                <ul className="flex flex-wrap gap-2 text-sm">
                  {draft.weaponSkills.map((skill, index) => (
                    <li key={`${skill.tier}:${skill.name}`}>
                      <button
                        type="button"
                        title="remove"
                        onClick={() =>
                          change({
                            weaponSkills: draft.weaponSkills.filter((_, at) => at !== index),
                            // A specialisation rests on the skill under it, so
                            // removing the skill removes what it paid for.
                            ...(draft.specialisedIn === skill.name ? { specialisedIn: '' } : {}),
                          })
                        }
                        className="rounded-md border border-neutral-300 px-3 py-1 dark:border-neutral-700"
                      >
                        {optionLabel(skill.name)}{' '}
                        <span className="text-xs text-neutral-600 dark:text-neutral-300">
                          {optionLabel(skill.tier)} ✕
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <p className="text-sm font-medium">A combat method</p>
              {/* The three printed sentences, shown rather than summarised:
                  they are what decides, and the server quotes them back. */}
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                {combat?.methodRules['singleClassFighter']}
              </p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {(combat?.methods ?? []).map((method) => {
                  const blockers = methodBlockers(draft, options, method);
                  const chosen = draft.combatMethods.some(
                    (one) => one.name === method.name && one.category === method.category,
                  );
                  return (
                    <li key={`${method.category}:${method.name}`}>
                      <button
                        type="button"
                        disabled={blockers.length > 0}
                        title={blockers.join('; ')}
                        onClick={() =>
                          change({
                            combatMethods: chosen
                              ? draft.combatMethods.filter(
                                  (one) =>
                                    !(one.name === method.name && one.category === method.category),
                                )
                              : [
                                  ...draft.combatMethods,
                                  { name: method.name, category: method.category },
                                ],
                          })
                        }
                        className={chip(chosen, blockers.length > 0)}
                      >
                        {optionLabel(method.name)}{' '}
                        <span className="text-xs text-neutral-600 dark:text-neutral-300">
                          {method.category}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Single-class fighters only, and only in a weapon already
                trained: "a fighter may specialize with any particular melee
                weapon, bow or crossbow **that he is otherwise skilled with its
                use**". So the list is what was bought above, and it is empty
                until something is. */}
            {draft.characterClass === 'fighter' && (
              <div className="space-y-1 border-t border-neutral-200 pt-4 dark:border-neutral-800">
                <label className="text-sm font-medium" htmlFor="specialise-in">
                  Specialise in one of them
                </label>
                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                  Fighters only, in a weapon you have already trained with. +1 to hit, +2 to
                  damage, and a better attack rate — one more point for a melee weapon, two for a
                  bow or crossbow.
                </p>
                <select
                  id="specialise-in"
                  value={draft.specialisedIn}
                  onChange={(e) => change({ specialisedIn: e.target.value })}
                  className="block rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <option value="">not specialised</option>
                  {/* ⚠ **Unaffordable weapons used to sit here as plain
                      options** — nothing stopped picking one the budget
                      couldn't cover, the same gap Ambidexterity and Train
                      had. A weapon already spent on this way (`specialisedIn
                      === skill.name`) always stays listed, or unpicking it
                      would be the only way back in. */}
                  {draft.weaponSkills
                    .filter((skill) => skill.tier === 'singleWeapon')
                    .filter(
                      (skill) =>
                        skill.name === draft.specialisedIn ||
                        affordsCsp(draft, options, combat?.specialisationCost?.[skill.name] ?? 0),
                    )
                    .map((skill) => (
                      <option key={skill.name} value={skill.name}>
                        {optionLabel(skill.name)}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <label className="flex items-center gap-2 border-t border-neutral-200 pt-4 text-sm dark:border-neutral-800">
              <input
                type="checkbox"
                checked={draft.ambidextrous}
                disabled={
                  !draft.ambidextrous && !affordsCsp(draft, options, combat?.ambidexterity.cost ?? 0)
                }
                onChange={(e) => change({ ambidextrous: e.target.checked })}
              />
              <span>
                Ambidexterity — {combat?.ambidexterity.cost}{' '}
                <span className="text-xs text-neutral-600 dark:text-neutral-300">
                  {combat?.ambidexterity.eligibility}
                </span>
              </span>
            </label>
          </div>
        )}

        {here === 'appearance' && (
          <div className="space-y-2">
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              What they look like — leave it blank and the DM will write one for you. Either way
              it's only written once; after that it's yours to edit on the sheet.
            </p>
            <textarea
              value={draft.description}
              onChange={(e) => change({ description: e.target.value })}
              aria-label="Appearance"
              rows={3}
              maxLength={400}
              placeholder="Build, colouring, bearing, what they are wearing."
              className={`w-full max-w-lg ${field}`}
            />
            <p className="text-xs tabular-nums text-neutral-600 dark:text-neutral-300">
              {draft.description.trim().length}/400
            </p>
          </div>
        )}

        {here === 'review' && (
          <div className="space-y-2">
            {/* ⚠ **The answer to the standing objection to wizards.** Nothing
                is hidden behind a Next button at the end: the whole character
                is here, and every line of it is one click from the step that
                set it. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              The whole character — nothing else happens when you press the button below. The
              server checks it all again, and if something's wrong it will say which rule, not
              just which field.
            </p>
            <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-neutral-600 dark:text-neutral-300">Name</dt>
              <dd>{draft.name.trim()}</dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Race and class</dt>
              <dd>
                {optionLabel(draft.race)} {optionLabel(draft.characterClass)}
                {draft.specialistSchool ? ` (${optionLabel(draft.specialistSchool)})` : ''} ·{' '}
                {optionLabel(draft.alignment)}
              </dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Scores</dt>
              <dd className="tabular-nums">
                {options.abilities
                  .map((ability) => `${ability} ${adjusted?.[ability] ?? '?'}`)
                  .join(' · ')}
                {wholeNumber(draft.exceptionalStrength) !== undefined
                  ? ` · exceptional ${draft.exceptionalStrength}%`
                  : ''}
                <span className="ml-1 text-xs text-neutral-600 dark:text-neutral-300">
                  (after the racial adjustment; the server applies any aging)
                </span>
              </dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Bonus languages</dt>
              <dd>
                {draft.bonusLanguages.length > 0
                  ? draft.bonusLanguages.map((l) => optionLabel(l)).join(' · ')
                  : 'none'}
              </dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Body</dt>
              <dd>
                {draft.gender.trim()} · {draft.age} years · {draft.heightIn} in ·{' '}
                {draft.weightLb} lb
              </dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Combat training</dt>
              <dd>
                {[
                  ...draft.weaponSkills.map(
                    (skill) => `${optionLabel(skill.name)} (${optionLabel(skill.tier)})`,
                  ),
                  ...draft.combatMethods.map((one) => optionLabel(one.name)),
                  ...(draft.ambidextrous ? ['ambidextrous'] : []),
                  ...(draft.specialisedIn
                    ? [`specialised in ${optionLabel(draft.specialisedIn)}`]
                    : []),
                ].join(' · ') || 'nothing — untrained with every weapon'}
              </dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Appearance</dt>
              <dd>{draft.description.trim() || 'the DM will write one, once'}</dd>
              <dt className="text-neutral-600 dark:text-neutral-300">Hit points</dt>
              <dd>
                rolled by the server — one {optionLabel(draft.characterClass)} hit die plus the
                Constitution adjustment
              </dd>
            </dl>
            {aging && <p className="text-sm text-amber-800 dark:text-amber-300">{aging}</p>}
            {/* ⚠ **Repeated here because it is irreversible.** The step said it
                too, and this is the last screen before the character exists. */}
            {cspLeft > 0 && (
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {cspLeft} combat skill point{cspLeft === 1 ? '' : 's'} will be lost — they are
                spent at 1st level or not at all.
              </p>
            )}
          </div>
        )}
      </Wizard>
    </div>
  );
}
